import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Menu, X, Lightbulb, Home, History, FileText, BarChart3, ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import clsx from 'clsx'
import ThemeToggle from '../ThemeToggle'

interface NavItem {
  id: string
  path: string
  label: string
  icon: React.ReactNode
  description: string
}

interface NavSection {
  id: string
  title: string
  items: NavItem[]
  defaultOpen?: boolean
}

const navSections: NavSection[] = [
  {
    id: 'main',
    title: 'Main',
    defaultOpen: true,
    items: [
      {
        id: 'home',
        path: '/',
        label: 'home',
        icon: <Home className="h-5 w-5" strokeWidth={1.5} />,
        description: 'chat with shellby'
      }
    ]
  },
  {
    id: 'tools',
    title: 'Tools',
    defaultOpen: false,
    items: [
      {
        id: 'history',
        path: '/history',
        label: 'history',
        icon: <History className="h-5 w-5" strokeWidth={1.5} />,
        description: 'view past queries'
      },
      {
        id: 'templates',
        path: '/templates',
        label: 'templates',
        icon: <FileText className="h-5 w-5" strokeWidth={1.5} />,
        description: 'saved response templates'
      }
    ]
  },
  {
    id: 'insights',
    title: 'Insights',
    defaultOpen: false,
    items: [
      {
        id: 'analytics',
        path: '/analytics',
        label: 'analytics',
        icon: <BarChart3 className="h-5 w-5" strokeWidth={1.5} />,
        description: 'usage statistics'
      }
    ]
  }
]

export default function FloatingMenu() {
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    main: true,
    tools: false,
    insights: false
  })
  const navigate = useNavigate()
  const location = useLocation()
  const prefersReducedMotion = useReducedMotion()

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  const handleNavClick = (path: string) => {
    navigate(path)
    setIsOpen(false)
  }

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  return (
    <>
      {/* Floating Controls - Menu Button + Theme Toggle */}
      <div className="fixed top-4 left-4 z-40 flex items-center gap-2">
        {/* Menu Button with Glow */}
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-[var(--bbs-jadeite-electric)]/20 to-[var(--bbs-synthwave-lavender)]/20 rounded-[14px] blur-lg opacity-0 group-hover:opacity-75 transition-all duration-300" />
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
            className={clsx(
              'btn-glass relative flex items-center gap-2',
              'hover:scale-105'
            )}
            style={{ padding: 'var(--bbs-spacing-md) var(--bbs-spacing-xl)', borderRadius: 'var(--bbs-radius-md)' }}
            aria-label="Open menu"
          >
            <Menu className={clsx(
              'w-5 h-5 text-[var(--bbs-text-secondary)] transition-all duration-300',
              'group-hover:text-[var(--bbs-jadeite-electric)] group-hover:rotate-90'
            )} />
            <span className={clsx(
              'text-sm font-medium text-[var(--bbs-text-secondary)] whitespace-nowrap overflow-hidden transition-all duration-300',
              isExpanded ? 'max-w-20 opacity-100' : 'max-w-0 opacity-0'
            )}>
              menu
            </span>
          </button>
        </div>

        {/* Theme Toggle */}
        <ThemeToggle />
      </div>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Menu Panel - Dropdown Style */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            className={clsx(
              'card fixed top-4 left-4 md:top-6 md:left-6 z-50',
              'w-72 sm:w-80',
              'overflow-hidden flex flex-col'
            )}
            style={{ borderRadius: 'var(--bbs-radius-lg)' }}
            data-static-card
          >

            {/* Header */}
            <div className="relative z-10 flex items-center justify-between p-3 md:p-4">
              <button
                type="button"
                onClick={() => { navigate('/'); setIsOpen(false) }}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity group"
              >
                <div className="group-hover:scale-110 transition-transform duration-300">
                  <img src="/shellby.png" alt="Shellby" className="w-12 h-12" />
                </div>
                <div>
                  <h2 className="font-semibold text-[var(--bbs-text-primary)] group-hover:text-[var(--bbs-jadeite-electric)] transition-colors">Shellby</h2>
                  <p className="text-xs text-[var(--bbs-text-tertiary)] italic">ready to help</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-4 h-4 text-[var(--bbs-text-tertiary)] hover:text-[var(--bbs-synthwave-hot-pink)]" />
              </button>
            </div>

            {/* Navigation Sections with Accordions */}
            <div className="relative z-10 flex-1 overflow-y-auto px-3 md:px-4 pb-3 md:pb-4 space-y-1">
              {navSections.map((section) => (
                <div key={section.id}>
                  {/* Section Header - Accordion Toggle */}
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <h4 className="text-[var(--bbs-jadeite-electric)] font-medium text-xs uppercase tracking-wider">
                      {section.title}
                    </h4>
                    <ChevronDown
                      className={clsx(
                        'w-4 h-4 text-[var(--bbs-text-tertiary)] transition-transform duration-200',
                        openSections[section.id] && 'rotate-180'
                      )}
                    />
                  </button>

                  {/* Section Items - Collapsible */}
                  <AnimatePresence initial={false}>
                    {openSections[section.id] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-1 py-1">
                          {section.items.map((item) => {
                            const isActive = location.pathname === item.path
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => handleNavClick(item.path)}
                                className={clsx(
                                  'w-full flex items-center space-x-3 p-2.5 rounded-lg transition-all duration-200 group/item relative',
                                  isActive
                                    ? 'bg-white/10 border border-white/20 text-[var(--bbs-text-primary)]'
                                    : 'text-[var(--bbs-text-secondary)] hover:bg-white/5 hover:text-[var(--bbs-text-primary)]'
                                )}
                              >
                                <div className={clsx(
                                  'flex-shrink-0 transition-colors duration-200',
                                  isActive ? 'text-[var(--bbs-jadeite-electric)]' : 'text-[var(--bbs-text-tertiary)] group-hover/item:text-[var(--bbs-jadeite-electric)]'
                                )}>
                                  {item.icon}
                                </div>
                                <div className="flex-1 text-left min-w-0">
                                  <div className="font-medium text-sm">{item.label}</div>
                                  <div className="text-xs opacity-75 mt-0.5">{item.description}</div>
                                </div>
                                {isActive && (
                                  <div className="relative flex-shrink-0">
                                    <div className="w-2 h-2 rounded-full bg-[var(--bbs-jadeite-electric)] shadow-lg shadow-[var(--bbs-jadeite-electric)]/50" />
                                  </div>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              {/* Tips */}
              <div className="pt-2 border-t border-white/10 mt-2">
                <div className="flex items-center gap-2 p-2">
                  <Lightbulb className="w-4 h-4 text-[var(--bbs-jadeite-electric)]" />
                  <h4 className="text-[var(--bbs-jadeite-electric)] font-medium text-xs uppercase tracking-wider">
                    Tips
                  </h4>
                </div>
                <ul className="space-y-1.5 text-xs text-[var(--bbs-text-tertiary)] px-2 pb-2">
                  <li className="flex items-start gap-2">
                    <span className="text-[var(--bbs-jadeite-electric)]">•</span>
                    <span>Press <kbd className="px-1 py-0.5 rounded bg-white/10 text-[var(--bbs-text-secondary)]">Enter</kbd> to send</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[var(--bbs-jadeite-electric)]">•</span>
                    <span>Include specific details for better responses</span>
                  </li>
                </ul>
              </div>

              {/* Quick Actions */}
              <div className="pt-2 border-t border-white/10">
                <div className="text-xs text-[var(--bbs-text-secondary)] mb-2 px-2">quick actions</div>
                <div className="flex items-center gap-2 px-2">
                  <button
                    type="button"
                    onClick={() => { window.location.reload(); setIsOpen(false) }}
                    className="flex-1 px-3 py-2 bg-white/5 text-[var(--bbs-text-secondary)] rounded-lg text-xs hover:bg-white/10 hover:text-[var(--bbs-text-primary)] transition-all duration-200 font-medium"
                  >
                    refresh app
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('Clear all local data? This cannot be undone.')) {
                        localStorage.clear()
                        window.location.reload()
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-white/5 text-[var(--bbs-text-secondary)] rounded-lg text-xs hover:bg-white/10 hover:text-[var(--bbs-text-primary)] transition-all duration-200 font-medium"
                  >
                    clear cache
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
